import { useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarRange, Mail, MessageSquare, Send, Sparkles, Star } from 'lucide-react';
import { ScheduleCalendar, type AccentTone } from './ScheduleCalendar';
import type { ScheduleEntry } from '../../lib/scheduleParsing';

interface CombinedItem {
  kind: 'email' | 'social' | 'followup';
  label: string;
  secondary: string;
  origId: string;
}

interface MarketingCalendarViewProps {
  combined: ScheduleEntry<CombinedItem>[];
  emailCount: number;
  socialCount: number;
  followUpCount: number;
  /** Called with the ORIGINAL item id (e.g. "email-1") so the page can route */
  onItemClick: (origId: string) => void;
}

const TONE_FOR_KIND: Record<CombinedItem['kind'], AccentTone> = {
  email: 'emerald',
  social: 'blue',
  followup: 'pink',
};

export function MarketingCalendarView({
  combined,
  emailCount,
  socialCount,
  followUpCount,
  onItemClick,
}: MarketingCalendarViewProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const total = emailCount + socialCount + followUpCount;

  return (
    <div className="space-y-6">
      {/* Intro hero */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative rounded-2xl overflow-hidden"
        style={{
          background: '#0B1226',
          boxShadow:
            '0 0 40px rgba(37,99,235,0.2), 0 0 70px rgba(16,185,129,0.14), inset 0 1px 0 rgba(255,255,255,0.05)',
        }}
      >
        {/* Gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, rgba(37,99,235,0.22), rgba(139,92,246,0.14) 45%, rgba(16,185,129,0.18))',
          }}
        />

        {/* Glow */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-[8%] top-[20%] w-[55%] h-[65%] rounded-full blur-[95px] bg-blue-500/30" />
          <div className="absolute right-[4%] bottom-[-10%] w-[45%] h-[65%] rounded-full blur-[105px] bg-emerald-500/25" />
        </div>

        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:32px_32px]" />

        {/* Top highlight */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-300/70 to-transparent" />

        <div className="relative p-6 md:p-8">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg mb-4 bg-blue-500/15 border border-blue-400/50 shadow-[0_0_16px_rgba(37,99,235,0.3)]">
            <CalendarRange className="w-4 h-4 text-blue-200" />
            <span className="text-xs font-bold uppercase tracking-wider text-blue-100">
              Marketing Calendar
            </span>
          </div>

          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight mb-3">
            Your Full Launch Rollout
          </h2>
          <p className="text-base md:text-lg text-slate-100 leading-relaxed max-w-3xl">
            Every email, social post, follow-up, and the live webinar itself — mapped to the day
            it goes out. Click any item to jump to its content.
          </p>

          {/* Stat strip */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatChip
              icon={<Mail className="w-3.5 h-3.5" />}
              tone="emerald"
              label="Marketing Emails"
              count={emailCount}
            />
            <StatChip
              icon={<MessageSquare className="w-3.5 h-3.5" />}
              tone="blue"
              label="Social Posts"
              count={socialCount}
            />
            <StatChip
              icon={<Star className="w-3.5 h-3.5" fill="currentColor" />}
              tone="amber"
              label="Live Webinar"
              count="Thu · W2"
              isCountString
            />
            <StatChip
              icon={<Send className="w-3.5 h-3.5" />}
              tone="pink"
              label="Follow-Ups"
              count={followUpCount}
            />
          </div>
        </div>
      </motion.div>

      {/* Legend */}
      <div className="flex items-center gap-4 flex-wrap px-2">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
          <Sparkles className="w-3 h-3 text-slate-500" />
          Legend
        </div>
        <LegendDot tone="emerald" label="Marketing Email" />
        <LegendDot tone="blue" label="Social Post" />
        <LegendDot tone="pink" label="Follow-Up" />
        <div className="flex items-center gap-2">
          <Star className="w-3.5 h-3.5 text-amber-300" fill="currentColor" />
          <span className="text-xs text-slate-300 font-medium">Webinar · Live</span>
        </div>
      </div>

      {/* Unified calendar — breaks out of the page container for more room */}
      <div className="md:-mx-6 lg:-mx-16 xl:-mx-28">
        <ScheduleCalendar
          entries={combined}
          activeId={null}
          hoveredId={hoveredId}
          onItemClick={(id) => {
            // The calendar component emits ScheduleEntry.id ("cal-email-N"), but
            // we want the parent's click handler to receive the ORIGINAL item id
            // ("email-N") so it can route to the right tab.
            const entry = combined.find((e) => e.id === id);
            if (entry) onItemClick(entry.data.origId);
          }}
          onItemHover={setHoveredId}
          renderPrimary={(data) => data.label}
          renderSecondary={(data) => data.secondary}
          accentTone={(data) => TONE_FOR_KIND[data.kind]}
          showWebinarAnchor
          title={`${total} scheduled broadcasts`}
          subtitle="Hover to preview · click to open"
          countLabel={`${total} total`}
        />
      </div>
    </div>
  );
}

interface StatChipProps {
  icon: React.ReactNode;
  tone: AccentTone;
  label: string;
  count: number | string;
  isCountString?: boolean;
}

const CHIP_TONE: Record<AccentTone, { border: string; bg: string; iconBg: string; iconText: string; accent: string; glow: string }> = {
  blue:    { border: 'border-blue-400/55',    bg: 'bg-gradient-to-br from-blue-950/85 to-blue-900/50',       iconBg: 'bg-blue-500/40',    iconText: 'text-blue-100',    accent: 'text-blue-200',    glow: 'shadow-[0_0_24px_rgba(37,99,235,0.3)]' },
  violet:  { border: 'border-violet-400/55',  bg: 'bg-gradient-to-br from-violet-950/85 to-violet-900/50',   iconBg: 'bg-violet-500/40',  iconText: 'text-violet-100',  accent: 'text-violet-200',  glow: 'shadow-[0_0_24px_rgba(139,92,246,0.3)]' },
  emerald: { border: 'border-emerald-400/55', bg: 'bg-gradient-to-br from-emerald-950/85 to-emerald-900/50', iconBg: 'bg-emerald-500/40', iconText: 'text-emerald-100', accent: 'text-emerald-200', glow: 'shadow-[0_0_24px_rgba(16,185,129,0.3)]' },
  amber:   { border: 'border-amber-400/55',   bg: 'bg-gradient-to-br from-amber-950/85 to-amber-900/45',     iconBg: 'bg-amber-500/40',   iconText: 'text-amber-100',   accent: 'text-amber-200',   glow: 'shadow-[0_0_24px_rgba(245,158,11,0.32)]' },
  cyan:    { border: 'border-cyan-400/55',    bg: 'bg-gradient-to-br from-cyan-950/85 to-cyan-900/50',       iconBg: 'bg-cyan-500/40',    iconText: 'text-cyan-100',    accent: 'text-cyan-200',    glow: 'shadow-[0_0_24px_rgba(34,211,238,0.3)]' },
  pink:    { border: 'border-pink-300/60',    bg: 'bg-gradient-to-br from-pink-950/85 to-pink-900/50',       iconBg: 'bg-pink-400/40',    iconText: 'text-pink-100',    accent: 'text-pink-200',    glow: 'shadow-[0_0_24px_rgba(244,143,177,0.35)]' },
};

function StatChip({ icon, tone, label, count, isCountString }: StatChipProps) {
  const t = CHIP_TONE[tone];
  return (
    <div className={`rounded-xl border px-4 py-3.5 ${t.bg} ${t.border} ${t.glow}`}>
      <div className="flex items-center gap-2 mb-2">
        <span
          className={`flex items-center justify-center w-6 h-6 rounded-md border border-white/15 ${t.iconBg} ${t.iconText}`}
        >
          {icon}
        </span>
        <span className={`text-[10px] uppercase tracking-[0.22em] font-bold ${t.accent}`}>
          {label}
        </span>
      </div>
      <div className={`font-bold text-white leading-none ${isCountString ? 'text-lg' : 'text-2xl'}`}>
        {count}
      </div>
    </div>
  );
}

const LEGEND_DOT: Record<AccentTone, string> = {
  blue: 'bg-blue-400 shadow-[0_0_10px_rgba(96,165,250,0.6)]',
  violet: 'bg-violet-400 shadow-[0_0_10px_rgba(167,139,250,0.6)]',
  emerald: 'bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.6)]',
  amber: 'bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.6)]',
  cyan: 'bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.6)]',
  pink: 'bg-pink-300 shadow-[0_0_10px_rgba(244,143,177,0.7)]',
};

function LegendDot({ tone, label }: { tone: AccentTone; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`w-2 h-2 rounded-full ${LEGEND_DOT[tone]}`} />
      <span className="text-xs text-slate-300 font-medium">{label}</span>
    </div>
  );
}
